import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useResource, PageTitle, Panel, Loading, ErrorState, Metric, DemoBadge, Pill, EmptyState, fmtDateTime, fmtMoney, STATUS_TONE } from "../../components/app/Kit";
import { useAuth } from "../../context/AuthContext";

const chartTooltip = {
  contentStyle: {
    background: "#0A0F14",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 6,
    fontSize: 11,
    color: "#F7F9FC",
  },
};

export default function Dashboard() {
  const { user, org } = useAuth();
  const analytics = useResource("/analytics/summary");
  const insights = useResource("/insights");
  const appointments = useResource("/appointments");
  const activity = useResource("/activity");

  if (analytics.state === "loading") return <Loading rows={8} testId="dashboard-loading" />;
  if (analytics.state === "error")
    return <ErrorState message={analytics.error} onRetry={analytics.reload} testId="dashboard-error" />;

  const m = analytics.data.metrics;
  const upcoming = (appointments.data || [])
    .filter((a) => new Date(a.start) >= new Date() && a.status === "scheduled")
    .slice(0, 5);

  return (
    <div data-testid="dashboard-page">
      <PageTitle
        testId="dashboard-title"
        title={`Good to see you, ${user?.name?.split(" ")[0]}.`}
        sub={`${org?.name} — operational overview across leads, appointments, calls and revenue.`}
        action={<DemoBadge />}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric testId="metric-leads" label="Leads" value={m.leads} hint="All-time in this workspace" />
        <Metric testId="metric-appointments" label="Appointments" value={m.appointments} tone="cyan" />
        <Metric testId="metric-conversion" label="Conversion rate" value={m.conversion_rate} suffix="%" />
        <Metric testId="metric-missed-calls" label="Missed calls" value={m.missed_calls} tone="cyan" hint={`${m.recovered_leads} recovered`} />
        <Metric testId="metric-revenue" label="Revenue (paid)" value={m.revenue} hint="Demo invoices marked paid" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel testId="dashboard-leads-chart" title="Leads — last 14 days" action={<DemoBadge testId="leads-chart-demo" />}>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.data.series.leads}>
                <defs>
                  <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00FF66" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" stroke="#A6AFBA" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#A6AFBA" fontSize={10} tickLine={false} axisLine={false} width={26} />
                <Tooltip {...chartTooltip} />
                <Area type="monotone" dataKey="value" stroke="#00FF66" strokeWidth={2} fill="url(#gLeads)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel
          testId="dashboard-insights"
          title="AI insights"
          action={<DemoBadge testId="insights-demo" label="Derived from demo data" />}
        >
          {insights.state === "loading" ? (
            <Loading rows={4} testId="insights-loading" />
          ) : insights.state === "error" ? (
            <ErrorState message={insights.error} onRetry={insights.reload} testId="insights-error" />
          ) : insights.data.insights.length === 0 ? (
            <EmptyState icon="Sparkles" title="No insights yet" sub="Insights appear once there is activity to analyse." testId="insights-empty" />
          ) : (
            <ul className="grid gap-2.5">
              {insights.data.insights.map((i, idx) => (
                <li
                  key={idx}
                  data-testid={`insight-${idx}`}
                  className="flex items-start gap-2.5 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs text-elv-dim"
                >
                  {i.tone === "warning" ? (
                    <Icons.AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
                  ) : (
                    <Icons.Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
                  )}
                  {i.text}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel
          testId="dashboard-pipeline"
          title="Pipeline"
          action={
            <Link to="/app/pipeline" data-testid="dashboard-pipeline-link" className="text-[11px] text-elv-green hover:text-elv-cyan">
              Open pipeline
            </Link>
          }
        >
          <div className="grid gap-2">
            {analytics.data.pipeline.map((s) => (
              <div key={s.stage} data-testid={`dashboard-stage-${s.stage}`} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-xs text-elv-dim">
                  <Pill tone={STATUS_TONE[s.stage] || "dim"}>{s.stage}</Pill>
                </span>
                <span className="text-xs text-elv-text">
                  {s.count} · {fmtMoney(s.value)}
                </span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          testId="dashboard-upcoming"
          title="Upcoming appointments"
          action={
            <Link to="/app/appointments" data-testid="dashboard-appointments-link" className="text-[11px] text-elv-green hover:text-elv-cyan">
              Calendar
            </Link>
          }
        >
          {upcoming.length === 0 ? (
            <EmptyState
              icon="CalendarCheck"
              title="No appointments"
              sub="Connect your calendar to start booking automatically."
              action={
                <Link to="/app/integrations" data-testid="dashboard-connect-calendar" className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]">
                  Connect Google Calendar
                </Link>
              }
              testId="dashboard-upcoming-empty"
            />
          ) : (
            <ul className="grid gap-2">
              {upcoming.map((a) => (
                <li key={a.id} data-testid={`dashboard-appointment-${a.id}`} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-elv-text">{a.customer_name}</p>
                    <Pill tone={STATUS_TONE[a.status]}>{a.status}</Pill>
                  </div>
                  <p className="mt-1.5 text-[11px] text-elv-dim">
                    {a.service} · {fmtDateTime(a.start)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel testId="dashboard-activity" title="Recent activity">
          {activity.state === "loading" ? (
            <Loading rows={5} testId="activity-loading" />
          ) : (activity.data || []).length === 0 ? (
            <EmptyState icon="Activity" title="No activity yet" sub="Automation runs and AI actions will appear here." testId="activity-empty" />
          ) : (
            <ul className="grid gap-2.5">
              {(activity.data || []).slice(0, 7).map((a) => (
                <li key={a.id} data-testid={`activity-${a.id}`} className="flex items-start justify-between gap-3 text-xs">
                  <span className="text-elv-dim">{a.title}</span>
                  <span className="shrink-0 font-mono text-[10px] text-elv-dim">{fmtDateTime(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Voice provider (Vapi)", "Vapi not configured.", "/app/integrations", "Connect Voice Provider"],
          ["Calendar", "Google Calendar not connected.", "/app/integrations", "Connect Calendar"],
          ["Messaging", "Twilio / WhatsApp not connected.", "/app/integrations", "Connect Messaging"],
          ["Payments", "Stripe not connected.", "/app/payments", "Connect Stripe"],
        ].map(([title, note, to, cta], i) => (
          <div key={title} data-testid={`connect-card-${i}`} className="glass glass-hover rounded-lg p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">{title}</p>
            <p className="mt-3 text-xs text-amber-200">{note}</p>
            <Link
              to={to}
              data-testid={`connect-card-cta-${i}`}
              className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan"
            >
              {cta} <Icons.ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
